/**
 * Connection Status Badge
 *
 * Sidebar indicator showing the current service connection state.
 * Combines WebSocket connectivity with position data freshness
 * to detect when the edge device / Docker is actually down.
 */

import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useServiceConnectionStatus, ServiceStatus } from '@/hooks/useServiceConnectionStatus';

interface ConnectionStatusBadgeProps {
  isCollapsed: boolean;
}

const STATUS_CONFIG: Record<ServiceStatus, { labelKey: string; dotClass: string }> = {
  online: { labelKey: 'connection.online', dotClass: 'bg-success' },
  connecting: { labelKey: 'connection.connecting', dotClass: 'bg-success animate-pulse' },
  offline: { labelKey: 'connection.offline', dotClass: 'bg-destructive' },
  disconnected: { labelKey: 'connection.disconnected', dotClass: 'bg-destructive' },
};

function ConnectionStatusBadge({ isCollapsed }: ConnectionStatusBadgeProps): JSX.Element {
  const { t } = useTranslation();
  const status = useServiceConnectionStatus();

  const cfg = STATUS_CONFIG[status];

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground',
        isCollapsed && 'justify-center px-0'
      )}
      title={isCollapsed ? t(cfg.labelKey) : undefined}
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', cfg.dotClass)} />
      {!isCollapsed && <span>{t(cfg.labelKey)}</span>}
    </div>
  );
}

export { ConnectionStatusBadge };
